import { useCallback, useEffect, useRef, useState } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../../services/firebase.config";
import { useAuth } from "../../services/AuthContext";
import { useLoadingManager } from "../../services/LoadingManager";
import dataCache from "../../services/DataCache";
import remoteQuizService from "../../services/RemoteQuizService";
import { calculatePoliticalAlignmentCategory } from "../../utils/derivedFields";
import {
  Groups,
  Balance,
  Person,
  AccountBalance,
  Handshake,
  Nature,
  Engineering,
  Psychology,
  AutoAwesome,
} from "@mui/icons-material";

// Calculate big 3 traits from quiz data (shared with profile logic)
const calculateBig3Traits = (quizData) => {
  const traits = { personality: null, political: null, thinking: null };

  quizData.forEach((quiz) => {
    if (quiz.quizType === "personality-check") {
      const introversionResults = quiz.results.filter(
        (r) => r.field === "introversion"
      );
      if (introversionResults.length > 0) {
        const averagePercentage = Math.round(
          introversionResults.reduce((sum, r) => sum + r.percentage1, 0) /
            introversionResults.length
        );
        if (averagePercentage <= 33)
          traits.personality = { label: "Extrovert", icon: Groups };
        else if (averagePercentage <= 66)
          traits.personality = { label: "Ambivert", icon: Balance };
        else traits.personality = { label: "Introvert", icon: Person };
      }

      const intuitionResults = quiz.results.filter(
        (r) => r.field === "intuition"
      );
      if (intuitionResults.length > 0) {
        const averagePercentage = Math.round(
          intuitionResults.reduce((sum, r) => sum + r.percentage1, 0) /
            intuitionResults.length
        );
        if (averagePercentage <= 33)
          traits.thinking = { label: "Concrete", icon: Engineering };
        else if (averagePercentage <= 66)
          traits.thinking = { label: "Balanced", icon: Psychology };
        else traits.thinking = { label: "Abstract", icon: AutoAwesome };
      }
    }

    if (quiz.quizType === "political-alignment") {
      const politicalFields = [
        "socialLib",
        "econLib",
        "globalism",
        "conservation",
        "public",
        "socialSupport",
      ];
      const politicalResults = quiz.results.filter((r) =>
        politicalFields.includes(r.field)
      );
      if (politicalResults.length > 0) {
        const fieldScores = {};
        politicalResults.forEach((result) => {
          if (!fieldScores[result.field]) fieldScores[result.field] = [];
          fieldScores[result.field].push(result.percentage1);
        });
        const processedScores = {};
        Object.entries(fieldScores).forEach(([field, percentages]) => {
          const average =
            percentages.reduce((sum, percent) => sum + percent, 0) /
            percentages.length;
          processedScores[field] = Math.round(average);
        });
        const alignment = calculatePoliticalAlignmentCategory(processedScores);
        if (alignment === "Liberal")
          traits.political = { label: "Liberal", icon: Nature };
        else if (alignment === "Conservative")
          traits.political = { label: "Conservative", icon: AccountBalance };
        else traits.political = { label: "Moderate", icon: Handshake };
      }
    }
  });

  return traits;
};

export const useFriendsData = () => {
  const { currentUserState } = useAuth();
  const loadingManager = useLoadingManager();

  const [userProfile, setUserProfile] = useState(null);
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [friendsBig3, setFriendsBig3] = useState({});

  const currentUserRef = useRef(currentUserState);
  const loadingManagerRef = useRef(loadingManager);

  useEffect(() => {
    currentUserRef.current = currentUserState;
    loadingManagerRef.current = loadingManager;
  }, [currentUserState, loadingManager]);

  // Helper to invalidate friend-related caches
  const invalidateFriendCaches = useCallback((userId) => {
    dataCache.delete(`friends-list-${userId}`);
    dataCache.delete(`incoming-requests-${userId}`);
    dataCache.delete(`outgoing-requests-${userId}`);
    dataCache.delete(`user-profile-${userId}`);
  }, []);

  // Fetch big 3 data for a friend with caching
  const fetchFriendBig3 = useCallback(async (friendId) => {
    try {
      let profileData = dataCache.getUserProfile(friendId);
      if (!profileData) {
        const profileRef = doc(db, "public-profiles", friendId);
        const profileSnap = await getDoc(profileRef);
        if (!profileSnap.exists()) return null;
        profileData = profileSnap.data();
        dataCache.setUserProfile(friendId, profileData);
      }

      const quizData = [];
      // Big 3 only uses personality-check and political-alignment quizzes
      const big3QuizTypes = ["personality-check", "political-alignment"];

      for (const quizType of big3QuizTypes) {
        const config = await remoteQuizService.getQuizConfig(quizType);
        if (!config) continue;

        const quizId = profileData[config.databaseField];
        if (quizId) {
          try {
            const quizDocRef = doc(db, "quiz-answers", quizId);
            const quizDoc = await getDoc(quizDocRef);
            if (quizDoc.exists() && quizDoc.data().status === "completed") {
              const quizAnswers = quizDoc.data().answers;
              const results = Object.values(quizAnswers).map((answer) => ({
                field: answer.field,
                percentage1: answer.percentage,
              }));
              quizData.push({ quizType, results });
            }
          } catch (_) {}
        }
      }

      return calculateBig3Traits(quizData);
    } catch (_) {
      return null;
    }
  }, []);

  const loadFriendsAndRequests = useCallback(async () => {
    const user = currentUserRef.current;
    const loading = loadingManagerRef.current;
    if (!user) return;

    try {
      loading.setLoading("friends-load");

      let profileData = dataCache.getUserProfile(user.uid);
      if (!profileData) {
        const profileRef = doc(db, "public-profiles", user.uid);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          profileData = profileSnap.data();
          dataCache.setUserProfile(user.uid, profileData);
        }
      }

      if (profileData) {
        setUserProfile(profileData);
        const friendIds = profileData.friends || [];
        let incomingRequestIds = profileData.friendRequests?.received || [];
        const outgoingRequestIds = profileData.friendRequests?.sent || [];

        // Auto-repair missing received requests
        try {
          const usersQuery = query(collection(db, "public-profiles"));
          const allUsersSnapshot = await getDocs(usersQuery);
          const missingRequests = [];
          allUsersSnapshot.forEach((docSnap) => {
            const userData = docSnap.data();
            const sentRequests = userData.friendRequests?.sent || [];
            if (
              sentRequests.includes(user.uid) &&
              !incomingRequestIds.includes(docSnap.id)
            ) {
              missingRequests.push(docSnap.id);
            }
          });
          if (missingRequests.length > 0) {
            const profileRef = doc(db, "public-profiles", user.uid);
            await updateDoc(profileRef, {
              "friendRequests.received": arrayUnion(...missingRequests),
            });
            incomingRequestIds = [...incomingRequestIds, ...missingRequests];
          }
        } catch (_) {}

        if (friendIds.length > 0) {
          const friendsCacheKey = `friends-list-${user.uid}`;
          let cachedFriends = dataCache.get(friendsCacheKey);
          let validFriends;
          if (cachedFriends) {
            setFriends(cachedFriends);
            validFriends = cachedFriends;
          } else {
            const friendPromises = friendIds.map(async (friendId) => {
              let friendData = dataCache.getUserProfile(friendId);
              if (!friendData) {
                const friendRef = doc(db, "public-profiles", friendId);
                const friendSnap = await getDoc(friendRef);
                if (friendSnap.exists()) {
                  friendData = friendSnap.data();
                  dataCache.setUserProfile(friendId, friendData);
                }
              }
              return friendData ? { id: friendId, ...friendData } : null;
            });

            const friendProfiles = await Promise.all(friendPromises);
            validFriends = friendProfiles.filter(Boolean);
            validFriends.sort((a, b) =>
              (a.displayName || "").localeCompare(b.displayName || "")
            );
            dataCache.set(friendsCacheKey, validFriends, 3 * 60 * 1000);
            setFriends(validFriends);
          }

          const big3Promises = validFriends.map(async (friend) => {
            const big3Data = await fetchFriendBig3(friend.id);
            return { friendId: friend.id, big3Data };
          });
          const big3Results = await Promise.all(big3Promises);
          const big3Map = {};
          big3Results.forEach(({ friendId, big3Data }) => {
            if (big3Data) big3Map[friendId] = big3Data;
          });
          setFriendsBig3(big3Map);
        } else {
          setFriends([]);
          setFriendsBig3({});
        }

        // Incoming requests
        if (incomingRequestIds.length > 0) {
          const incomingCacheKey = `incoming-requests-${user.uid}`;
          let cachedIncoming = dataCache.get(incomingCacheKey);
          if (cachedIncoming) {
            setIncomingRequests(cachedIncoming);
          } else {
            const requestPromises = incomingRequestIds.map(
              async (requesterId) => {
                let requesterData = dataCache.getUserProfile(requesterId);
                if (!requesterData) {
                  const requesterRef = doc(db, "public-profiles", requesterId);
                  const requesterSnap = await getDoc(requesterRef);
                  if (requesterSnap.exists()) {
                    requesterData = requesterSnap.data();
                    dataCache.setUserProfile(requesterId, requesterData);
                  }
                }
                return requesterData
                  ? { id: requesterId, ...requesterData }
                  : null;
              }
            );
            const requestProfiles = await Promise.all(requestPromises);
            const validRequests = requestProfiles.filter(Boolean);
            dataCache.set(incomingCacheKey, validRequests, 3 * 60 * 1000);
            setIncomingRequests(validRequests);
          }
        } else {
          setIncomingRequests([]);
        }

        // Outgoing requests
        if (outgoingRequestIds.length > 0) {
          const outgoingCacheKey = `outgoing-requests-${user.uid}`;
          let cachedOutgoing = dataCache.get(outgoingCacheKey);
          if (cachedOutgoing) {
            setOutgoingRequests(cachedOutgoing);
          } else {
            const outgoingPromises = outgoingRequestIds.map(
              async (recipientId) => {
                let recipientData = dataCache.getUserProfile(recipientId);
                if (!recipientData) {
                  const recipientRef = doc(db, "public-profiles", recipientId);
                  const recipientSnap = await getDoc(recipientRef);
                  if (recipientSnap.exists()) {
                    recipientData = recipientSnap.data();
                    dataCache.setUserProfile(recipientId, recipientData);
                  }
                }
                return recipientData
                  ? { id: recipientId, ...recipientData }
                  : null;
              }
            );
            const outgoingProfiles = await Promise.all(outgoingPromises);
            const validOutgoing = outgoingProfiles.filter(Boolean);
            dataCache.set(outgoingCacheKey, validOutgoing, 3 * 60 * 1000);
            setOutgoingRequests(validOutgoing);
          }
        } else {
          setOutgoingRequests([]);
        }
      }

      loading.setSuccess("friends-load");
    } catch (error) {
      loading.setError("friends-load", error);
    }
  }, [fetchFriendBig3]);

  // Load on mount
  useEffect(() => {
    loadFriendsAndRequests();
  }, [loadFriendsAndRequests]);

  // Real-time refresh on tab focus/visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUserRef.current) {
        invalidateFriendCaches(currentUserRef.current.uid);
        loadFriendsAndRequests();
      }
    };
    const handleFocus = () => {
      if (currentUserRef.current) {
        invalidateFriendCaches(currentUserRef.current.uid);
        loadFriendsAndRequests();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadFriendsAndRequests, invalidateFriendCaches]);

  return {
    userProfile,
    friends,
    incomingRequests,
    outgoingRequests,
    friendsBig3,
    loadFriendsAndRequests,
    invalidateFriendCaches,
  };
};
